import { UIElement } from "../common_ui_elements/ui_element.js";
import { Vector2 } from "../../common/vector2.js";
import { OverlayId } from "./overlay_ids.js";
import type { SurfaceAppearMotion, SurfaceCloseMotion } from "../dom_backend/surface_motion.js";

export abstract class AbstractOverlay extends UIElement
{
  protected params: any;
  protected closeOnBackgroundClick: boolean;
  private overlay_id: OverlayId;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, overlay_id: OverlayId, params?: any, closeOnBackgroundClick: boolean = true )
  {
    super( ctx, x, y, width, height );
    this.overlay_id = overlay_id;
    this.params = params;
    this.closeOnBackgroundClick = closeOnBackgroundClick;
  }

  public getId(): OverlayId
  {
    return this.overlay_id;
  }

  public update( now: number ): void
  {
    this.updateBasic( now );
  }

  abstract render(): void;
  abstract name(): string;

  public onOverlayWillClose(): void {}

  private surface_animation: Animation | null = null;

  public setSurfaceAnimation( anim: Animation | null ): void
  {
    this.cancelSurfaceAnimation();
    this.surface_animation = anim;
  }

  public cancelSurfaceAnimation(): void
  {
    if ( !this.surface_animation )
      return;
    this.surface_animation.cancel();
    this.surface_animation = null;
  }

  public containsPointGlobal( p: Vector2 ): boolean
  {
    const pixel_pos = this.pixelPosForRender();

    const x = pixel_pos.x + this.rect.getX();
    const y = pixel_pos.y + this.rect.getY();
    const w = this.rect.getW();
    const h = this.rect.getH();

    return p.x > x
      && p.y > y
      && p.x < x + w
      && p.y < y + h;
  }

  public getCenterGlobal(): Vector2
  {
    const pixel_pos = this.pixelPosForRender();
    return new Vector2(
      pixel_pos.x + this.rect.getX() + this.rect.getW() / 2,
      pixel_pos.y + this.rect.getY() + this.rect.getH() / 2
    );
  }

  public getCloseOnBackgroundClick(): boolean
  {
    return this.closeOnBackgroundClick;
  }

  public closesOnBackButton(): boolean
  {
    return true;
  }

  public getAppearMotion(): SurfaceAppearMotion | null
  {
    return { from_scale: 0.92, scale_up_ms: 160 };
  }

  public getCloseMotion(): SurfaceCloseMotion | null
  {
    return { to_scale: 0.92, duration_ms: 120 };
  }

  public getParams(): any
  {
    return this.params;
  }

  /**
   * DOM mode only: a closed overlay is hidden into OverlayManager's pool
   * instead of being destroyed, and the next open with the same poolKey()
   * reuses it (OverlayFactory). Building one cost two 50-60 ms frames at CPU x4.
   */
  public isPoolable(): boolean
  {
    return false;
  }

  /** Overlays with equal keys have the same structure; onReuse only refreshes them. */
  public poolKey(): string
  {
    return this.name();
  }

  /**
   * Takes the new params and replays the appearance. Throws when they need a
   * different structure; the factory then builds a new overlay.
   */
  public onReuse( _params: unknown ): void
  {
  }

  private reused = false;

  public wasReused(): boolean
  {
    return this.reused;
  }

  public resetForReuse(): void
  {
    this.reused = true;
    this.cancelSurfaceAnimation();
    if ( this.isDomMode() )
    {
      const node = this.getDomNode();
      node.style.opacity = "";
      node.style.scale = "";
      this.setDomHidden( false );
    }
    this.invalidateSubtree();
  }

  public hideIntoPool(): void
  {
    this.cancelSurfaceAnimation();
    if ( this.isDomMode() )
    {
      this.setDomHidden( true );
      this.getDomNode().style.display = "none";
    }
  }

  /**
   * Optional CSS background used outside the centered game canvas.
   * Regular modal overlays keep the dimmed backdrop from style.css.
   */
  public getViewportBackground(): string | null
  {
    return null;
  }
}
